import { zSchema } from "../../schema-validation";
import { ECitizenshipStatus } from "../ECitizenshipStatus";
import { EDocumentField } from "../EDocumentField";
import { EDocumentKey, EDocumentList } from "../EDocumentKey";
import { II9DocumentTypeFormDataProps } from "../II9Document";
import {
  citizenshipStatusIndependentDocumentTypeZodSchemaMap,
  documentFieldZodSchemaMap,
  documentTypeZodSchemaMap,
} from "./document-config";
import {
  citizenshipStatusIndependentDocumentTypeZodSchemaMapSpanish,
  documentFieldZodSchemaMapSpanish,
  documentTypeZodSchemaMapSpanish,
} from "./document-config-spanish";

export const otherNamesForDocumentNumber = [
  EDocumentField.US_PASSPORT_NUMBER,
  EDocumentField.FOREIGN_PASSPORT_NUMBER,
  EDocumentField.I766_NUMBER,
  EDocumentField.I94_NUMBER,
  EDocumentField.DOCUMENT_BC_NUMBER,
];

export const otherNamesForIssuingAuthority = [
  EDocumentField.COUNTRY_CODE,
  EDocumentField.US_STATE_CODE,
];

/**
 * Manually scraped from https://www.uscis.gov/i-9-central/form-i-9-resources/handbook-for-employers-m-274/130-acceptable-documents-for-verifying-employment-authorization-and-identity
 * and other government sources / example images
 */
export const documentsWithoutExpirationDate = [
  EDocumentKey.DAY_CARE_RECORD,
  EDocumentKey.DS_1350,
  EDocumentKey.FORM_I179,
  EDocumentKey.FORM_I197,
  EDocumentKey.FS_240,
  EDocumentKey.FS_545,
  EDocumentKey.HOSPITAL_RECORD,
  EDocumentKey.NATIVE_AMERICAN_TRIBAL_DOCUMENT,
  EDocumentKey.SCHOOL_RECORD,
  EDocumentKey.SOCIAL_SECURITY_CARD,
  EDocumentKey.US_BIRTH_CERTIFICATE,
  EDocumentKey.VOTER_REGISTRATION_CARD,
];

export interface IGetI9DocumentFormFieldsProps
  extends II9DocumentTypeFormDataProps {
  citizenship_status: ECitizenshipStatus;
  spanishMode?: boolean;
  workerInfo?: WorkerInfo;
  withSuffix?: boolean;
}

export type IGetI9DocumentFormZodSchemasReturn = {
  documentTypeAZodSchema?: zSchema.ZodObject<any, any>;
  documentTypeBZodSchema?: zSchema.ZodObject<any, any>;
  documentTypeCZodSchema?: zSchema.ZodObject<any, any>;
};

type WorkerInfo = {
  ssn?: string;
};

export const getAdditionalFieldsZodSchema = (
  zodSchema: zSchema.ZodObject<any, any>,
  documentKey: EDocumentKey,
  additionalFieldSuffix: string = "",
  spanishMode: boolean = false,
  workerInfo: WorkerInfo = {}
) => {
  const documentFieldZodSchema = spanishMode
    ? documentFieldZodSchemaMapSpanish
    : documentFieldZodSchemaMap;

  let fieldNames: string[] = [];
  if (isZodDiscriminatedUnion(zodSchema)) {
    fieldNames = zodSchema.options.reduce(
      (acc: string[], option: zSchema.ZodObject<any, any>) => [
        ...acc,
        ...Object.keys(option.shape ?? {}),
      ],
      []
    );
  } else {
    fieldNames = Object.keys(zodSchema?.shape ?? {}) ?? [];
  }

  const doesDocumentNumberExist = fieldNames.some((field) =>
    otherNamesForDocumentNumber.includes(field as EDocumentField)
  );
  const doesIssuingAuthorityExist = fieldNames.some((field) =>
    otherNamesForIssuingAuthority.includes(field as EDocumentField)
  );
  const doesExpirationDateAlreadyExistInFieldNames = fieldNames.some(
    (field) => field === EDocumentField.EXPIRATION_DATE
  );
  const documentHasExpirationDate =
    !documentsWithoutExpirationDate.includes(documentKey);
  const isSSNDocument = documentKey === EDocumentKey.SOCIAL_SECURITY_CARD;

  const additionalFieldsZodSchema = {
    ...(doesIssuingAuthorityExist
      ? {}
      : {
          [`${EDocumentField.ISSUING_AUTHORITY}${additionalFieldSuffix}`]:
            documentFieldZodSchema[EDocumentField.ISSUING_AUTHORITY],
        }),
    ...(doesDocumentNumberExist
      ? {}
      : {
          [`${EDocumentField.DOCUMENT_NUMBER}${additionalFieldSuffix}`]:
            documentFieldZodSchema[EDocumentField.DOCUMENT_NUMBER],
        }),
    ...(isSSNDocument
      ? {
          [`${EDocumentField.DOCUMENT_NUMBER}${additionalFieldSuffix}`]:
            documentFieldZodSchema[EDocumentField.SSN_NUMBER]?.refine(
              (data) =>
                data.replace(/-/g, "").replace(/ /g, "") ===
                workerInfo.ssn?.replace(/-/g, "").replace(/ /g, ""),
              spanishMode
                ? "El número de Seguro Social no coincide con la información personal."
                : "Social Security Number does not match personal information."
            ),
        }
      : {}),
    ...(doesExpirationDateAlreadyExistInFieldNames || !documentHasExpirationDate
      ? {}
      : {
          [`${EDocumentField.EXPIRATION_DATE}${additionalFieldSuffix}`]:
            documentFieldZodSchema[EDocumentField.EXPIRATION_DATE],
        }),
  };

  if (Object.keys(additionalFieldsZodSchema).length === 0) {
    return zodSchema;
  }

  if (isZodDiscriminatedUnion(zodSchema)) {
    return extendSchema(
      zodSchema,
      zSchema.object(additionalFieldsZodSchema)
    ) as zSchema.ZodObject<any, any>;
  }

  return (
    zodSchema?.extend(additionalFieldsZodSchema) ??
    zSchema.object(additionalFieldsZodSchema)
  );
};

/**
 * Get the zod schema config for the I9 document form
 *
 * Some of the fields we collect are dependent on the citizenship status AND document types selected
 */

export const getI9DocumentFormZodSchemas = ({
  citizenship_status,
  document_type_a_code,
  document_type_b_code,
  document_type_c_code,
  spanishMode,
  workerInfo,
  withSuffix = true,
}: IGetI9DocumentFormFieldsProps): IGetI9DocumentFormZodSchemasReturn => {
  const documentTypeZodSchema = spanishMode
    ? documentTypeZodSchemaMapSpanish[citizenship_status]
    : documentTypeZodSchemaMap[citizenship_status];
  if (document_type_a_code) {
    // Grab zod schema for document type A
    let zodSchema = documentTypeZodSchema?.[
      document_type_a_code
    ] as zSchema.ZodObject<any, any>;

    // Get the zod schema for the citizenship-independent fields
    const citizenshipIndependentZodSchema = spanishMode
      ? citizenshipStatusIndependentDocumentTypeZodSchemaMapSpanish[
          document_type_a_code
        ]
      : citizenshipStatusIndependentDocumentTypeZodSchemaMap[
          document_type_a_code
        ];

    // Merge the citizenship-independent zod schema into the zod schema
    if (citizenshipIndependentZodSchema) {
      zodSchema = zodSchema?.merge(citizenshipIndependentZodSchema);
    }

    const newZodSchema = getAdditionalFieldsZodSchema(
      zodSchema,
      document_type_a_code,
      "",
      spanishMode,
      workerInfo
    );

    return {
      documentTypeAZodSchema: newZodSchema,
    };
  }

  const getZodSchemaForTypeBOrC = (
    documentKey: EDocumentKey,
    documentList: EDocumentList
  ) => {
    /**
     * Get the individual zod schemas for list B/C documents
     */
    let zodSchema = documentTypeZodSchema?.[documentKey] as zSchema.ZodObject<
      any,
      any
    >;

    // Get the individual citizenship-independent zod schemas for list B/C documents
    const citizenshipIndependentZodSchema = spanishMode
      ? citizenshipStatusIndependentDocumentTypeZodSchemaMapSpanish[documentKey]
      : citizenshipStatusIndependentDocumentTypeZodSchemaMap[documentKey];

    // Merge the citizenship-independent fields into the zod schema
    if (citizenshipIndependentZodSchema) {
      zodSchema =
        zodSchema?.merge(citizenshipIndependentZodSchema) ||
        citizenshipIndependentZodSchema;
    }
    return getAdditionalFieldsZodSchema(
      zodSchema,
      documentKey,
      withSuffix
        ? documentList === EDocumentList.LIST_B
          ? "_type_b"
          : "_type_c"
        : "",
      spanishMode,
      workerInfo
    );
  };

  // If documents of type B and C are selected
  if (document_type_b_code && document_type_c_code) {
    return {
      documentTypeBZodSchema: getZodSchemaForTypeBOrC(
        document_type_b_code,
        EDocumentList.LIST_B
      ),
      documentTypeCZodSchema: getZodSchemaForTypeBOrC(
        document_type_c_code,
        EDocumentList.LIST_C
      ),
    };
  }

  // If only document of type B is selected (Persona flow switching from List A to List B/C)
  if (document_type_b_code && !document_type_c_code) {
    return {
      documentTypeBZodSchema: getZodSchemaForTypeBOrC(
        document_type_b_code,
        EDocumentList.LIST_B
      ),
    };
  }

  // If only document of type C is selected (Supplement B)
  if (document_type_c_code) {
    return {
      documentTypeCZodSchema: getZodSchemaForTypeBOrC(
        document_type_c_code,
        EDocumentList.LIST_C
      ),
    };
  }

  throw new Error("Invalid document type combination.");
};

/**
 * Safely extends a Zod schema with additional fields, handling both ZodObject and ZodDiscriminatedUnion
 */
export const extendSchema = (
  baseSchema:
    | zSchema.ZodObject<any, any>
    | zSchema.ZodDiscriminatedUnion<any, any>,
  extensionSchema: zSchema.ZodObject<any, any>
): zSchema.ZodObject<any, any> | zSchema.ZodDiscriminatedUnion<any, any> => {
  // If it's a regular ZodObject, use merge
  if (baseSchema instanceof zSchema.ZodObject) {
    return baseSchema.merge(extensionSchema);
  }

  // If it's a ZodDiscriminatedUnion, extend each option
  if (isZodDiscriminatedUnion(baseSchema)) {
    const typeAssertedSchema = baseSchema as zSchema.ZodDiscriminatedUnion<
      any,
      any
    >;
    const extendedOptions = typeAssertedSchema.options.map(
      (optionSchema: zSchema.ZodObject<any>) =>
        optionSchema.merge(extensionSchema)
    );
    return zSchema.discriminatedUnion(
      typeAssertedSchema.discriminator,
      extendedOptions as any
    );
  }

  return extensionSchema;
};

/**
 * Checks if a schema is a ZodDiscriminatedUnion
 */
export const isZodDiscriminatedUnion = (
  schema: any
): schema is zSchema.ZodDiscriminatedUnion<any, any> =>
  schema instanceof zSchema.ZodDiscriminatedUnion;
